import { createRouter, createWebHistory } from 'vue-router'
import { nextTick } from 'vue'
import { useBookingStore } from '../stores/booking'
import { useOrgStore, type OrgConfig } from '../stores/org'
import { routerBase } from '../lib/runtime'
import { isTableFlow } from '../lib/tableFlow'

function requireGuestDetails() {
  const store = useBookingStore()
  if (!store.venueId || !store.selection) return { name: 'venues' }
  if (!store.date) return { name: 'datetime-date' }
  if (store.selection.mode === 'pool_table' && (!store.timeSlot || !store.startTime)) {
    return { name: 'datetime-time' }
  }
  if (isTableFlow(store) && (!store.startTime || !store.guestsConfirmed)) return { name: 'datetime-date' }
  if (!store.startTime && !store.timeSlot) return { name: 'datetime-time' }
  if (!store.guestsConfirmed) return { name: 'guests' }
}

/**
 * Pages of a paid flow an organisation may not offer: reachable by a typed or bookmarked URL, so each is guarded
 * by the organisation's own feature flag (from /config, which the guard waits for). Without the feature the
 * customer lands on the venue page, never on a page of another business's fees and terms.
 */
function requireFeature(feature: keyof OrgConfig['features']) {
  return async (to: { params: Record<string, string | string[]> }) => {
    const org = useOrgStore()
    await org.load()
    if (org.features[feature]) return true
    const venueId = to.params.venueId
    return venueId ? { name: 'venue', params: { venueId } } : { name: 'home' }
  }
}

const router = createRouter({
  history: createWebHistory(routerBase()),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/venues',
      name: 'venues',
      component: () => import('../views/VenueSelectView.vue'),
    },
    {
      path: '/venue/:venueId',
      name: 'venue',
      component: () => import('../views/VenueView.vue'),
    },
    // The old large-group and function pages: a group now books through the one booking flow, so any saved
    // link starts it at the venue.
    {
      path: '/venue/:venueId/large-booking/:rest(.*)*',
      redirect: (to) => ({ name: 'venue', params: { venueId: to.params.venueId } }),
    },
    {
      path: '/function-terms',
      name: 'function-terms',
      component: () => import('../views/FunctionTermsView.vue'),
    },
    // Redirect the old single route so any saved links still work.
    {
      path: '/booking/datetime',
      redirect: '/booking/datetime/date',
    },
    {
      path: '/booking/datetime/date',
      name: 'datetime-date',
      component: () => import('../views/DateTimeView.vue'),
    },
    {
      path: '/booking/datetime/time',
      name: 'datetime-time',
      component: () => import('../views/DateTimeView.vue'),
      beforeEnter: () => {
        // If no date has been selected yet, send the user back to the date step. A table booking picks its
        // guests, date and time on the one screen.
        const store = useBookingStore()
        if (!store.date || isTableFlow(store)) return { name: 'datetime-date' }
      },
    },
    {
      path: '/booking/guests',
      name: 'guests',
      component: () => import('../views/GuestsView.vue'),
      beforeEnter: () => {
        const store = useBookingStore()
        if (!store.date || isTableFlow(store)) return { name: 'datetime-date' }
        if (!store.startTime && !store.timeSlot) return { name: 'datetime-time' }
      },
    },
    {
      path: '/booking/details',
      name: 'details',
      component: () => import('../views/DetailsView.vue'),
      beforeEnter: requireGuestDetails,
    },
    {
      path: '/booking/group',
      name: 'group',
      component: () => import('../views/GroupBookingView.vue'),
      // Venue hire needs the organisation's function bookings; a large group its large bookings.
      beforeEnter: [
        () => requireFeature(useBookingStore().largeBookingType === 'function_event' ? 'functionEvents' : 'largeBookings')({ params: {} }),
        requireGuestDetails,
      ],
    },
    {
      path: '/booking/pool-table-payment',
      name: 'pool-table-payment',
      component: () => import('../views/PoolTablePaymentView.vue'),
      beforeEnter: [requireFeature('poolTables'), requireGuestDetails],
    },
    {
      path: '/booking/deposit/:bookingId',
      name: 'deposit',
      component: () => import('../views/DepositView.vue'),
    },
    {
      path: '/booking/confirmation/:bookingId',
      name: 'confirmation',
      component: () => import('../views/ConfirmationView.vue'),
    },
    {
      path: '/booking/manage/:bookingId',
      name: 'manage',
      component: () => import('../views/ManageBookingView.vue'),
    },
    {
      path: '/booking/lookup',
      name: 'lookup',
      component: () => import('../views/BookingLookupView.vue'),
    },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

// When embedded in an iframe the iframe is auto-sized to its content height, so
// there is no internal scroll — the scrollbar lives in the parent window.
// scrollBehavior handles standalone use; this hook handles the iframe case.
router.afterEach(() => {
  if (window.parent && window.parent !== window) {
    // Use nextTick so the scroll fires after Vue has rendered the new route
    // component and the iframe has settled to its new height.
    nextTick(() => {
      try {
        // Same-origin: scroll the parent directly, subtracting the live header
        // height so the iframe top clears the fixed nav bar.
        const iframe = window.frameElement
        if (!iframe) throw new Error('no frameElement')
        const rect = iframe.getBoundingClientRect()
        const absoluteTop = rect.top + window.parent.scrollY
        const header = window.parent.document.querySelector('.h-guest-header, header.header')
        const headerOffset = (header ? (header as HTMLElement).offsetHeight : 0) + 16
        window.parent.scrollTo({ top: Math.max(0, absoluteTop - headerOffset), behavior: 'instant' })
      } catch {
        // Cross-origin (production): ask the parent to scroll via postMessage.
        window.parent.postMessage({ type: 'jb-booking-scroll-top' }, '*')
      }
    })
  }
})

export default router
